import React, { useState, useEffect, useCallback } from "react";
import { useToast } from "../Toast/ToastContext";
import {
  getSettings,
  toggleLimitOrderSetting,
  updatePnlSettings,
  updateTrailingStop,
} from "../../services/settingsService";
import { placeExitAllOrders } from "../../services/orderService";
import { isAuthenticated } from "../../services/auth";
import { fetchTokenHealth, formatTokenTime } from "../../services/tokenHealth";
import "./TradingControlsPanel.css";

const PNL_STEP_SIZE = 500;

const parsePnlValue = (value, fallback = 0) => {
  const number = parseInt(value, 10);
  return Number.isFinite(number) ? number : fallback;
};

const TradingControlsPanel = ({ openPositions = [], indexStreamData = {}, selectedIndex = "SENSEX" }) => {
  // Get current index data
  const indexData = indexStreamData?.[selectedIndex] || {};
  const { showSuccess, showError } = useToast();

  // Settings loading state
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Limit order state
  const [limitOrderEnabled, setLimitOrderEnabled] = useState(false);
  const [limitOrderPercent, setLimitOrderPercent] = useState(0.5);

  // Exit PnL (Stop) state
  const [exitPnlEnabled, setExitPnlEnabled] = useState(false);
  const [exitPnlValue, setExitPnlValue] = useState(-1000);
  const [editingExitPnl, setEditingExitPnl] = useState(false);
  const [lastSavedExitValue, setLastSavedExitValue] = useState(-1000);

  // Target PnL state
  const [targetPnlEnabled, setTargetPnlEnabled] = useState(false);
  const [targetPnlValue, setTargetPnlValue] = useState(2000);
  const [editingTargetPnl, setEditingTargetPnl] = useState(false);
  const [lastSavedTargetValue, setLastSavedTargetValue] = useState(2000);

  // Trailing stop state
  const [trailingEnabled, setTrailingEnabled] = useState(false);
  const [trailingPercent, setTrailingPercent] = useState(25);
  const [trailingActivation, setTrailingActivation] = useState(500);
  const [peakPnl, setPeakPnl] = useState(0);
  const [showTrailingModal, setShowTrailingModal] = useState(false);
  const [editTrailingPercent, setEditTrailingPercent] = useState(25);
  const [editTrailingActivation, setEditTrailingActivation] = useState(500);

  // Token health state
  const [tokenHealth, setTokenHealth] = useState(null);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!isAuthenticated()) return;

      try {
        const settings = await getSettings();

        const limitOrder = settings.limit_order || {};
        setLimitOrderEnabled(limitOrder.enabled || false);
        setLimitOrderPercent(limitOrder.value || 0.5);

        const exitPnl = settings.exit_pnl || {};
        const exitValue = exitPnl.value || -1000;
        setExitPnlEnabled(exitPnl.enabled || false);
        setExitPnlValue(exitValue);
        setLastSavedExitValue(exitValue);

        const targetPnl = settings.target_pnl || {};
        const targetValue = targetPnl.value || 2000;
        setTargetPnlEnabled(targetPnl.enabled || false);
        setTargetPnlValue(targetValue);
        setLastSavedTargetValue(targetValue);

        const trailingStop = settings.trailing_stop || {};
        setTrailingEnabled(trailingStop.enabled || false);
        setTrailingPercent(trailingStop.trailing_percent || 25);
        setTrailingActivation(trailingStop.activation_profit || 500);
        setPeakPnl(trailingStop.current_peak || 0);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchSettings();
  }, []);

  // Fetch token health on mount and periodically
  useEffect(() => {
    const loadTokenHealth = async () => {
      if (!isAuthenticated()) return;
      try {
        const health = await fetchTokenHealth();
        setTokenHealth(health);
      } catch (error) {
        console.error("Error fetching token health:", error);
      }
    };

    loadTokenHealth();
    // Refresh every 30 seconds
    const interval = setInterval(loadTokenHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  // Exit All handler
  const handleExitAll = useCallback(async () => {
    if (!isAuthenticated() || openPositions.length === 0) return;

    setIsExiting(true);
    try {
      const result = await placeExitAllOrders();
      if (result.success) {
        showSuccess(`Exited ${result.exited_count || result.total_exits || 0} positions`);
      } else {
        showError(result.message || "Failed to exit positions");
      }
    } catch (error) {
      showError(`Error exiting positions: ${error.message}`);
    } finally {
      setIsExiting(false);
    }
  }, [openPositions.length, showSuccess, showError]);

  // Toggle limit order
  const handleToggleLimitOrder = async (enabled, percent = limitOrderPercent) => {
    setIsSettingsLoading(true);
    try {
      await toggleLimitOrderSetting(enabled, percent);
      setLimitOrderEnabled(enabled);
      setLimitOrderPercent(percent);
      showSuccess(`Limit order ${enabled ? `enabled at ${percent}%` : "disabled"}`);
    } catch (error) {
      showError(`Failed to update limit order: ${error.message}`);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  // Toggle exit PnL (stop)
  const toggleExitPnl = async (enabled, value = exitPnlValue) => {
    const parsedValue = parsePnlValue(value, 0);
    setIsSettingsLoading(true);
    try {
      await updatePnlSettings("exit_pnl", enabled, parsedValue);
      setExitPnlEnabled(enabled);
      setExitPnlValue(parsedValue);
      if (enabled) {
        setLastSavedExitValue(parsedValue);
      }
      showSuccess(`Stop loss ${enabled ? `enabled at ${parsedValue}` : "disabled"}`);
    } catch (error) {
      showError(`Failed to update stop: ${error.message}`);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  // Toggle target PnL
  const toggleTargetPnl = async (enabled, value = targetPnlValue) => {
    const parsedValue = parsePnlValue(value, 0);
    setIsSettingsLoading(true);
    try {
      await updatePnlSettings("target_pnl", enabled, parsedValue);
      setTargetPnlEnabled(enabled);
      setTargetPnlValue(parsedValue);
      if (enabled) {
        setLastSavedTargetValue(parsedValue);
      }
      showSuccess(`Target ${enabled ? `enabled at ${parsedValue}` : "disabled"}`);
    } catch (error) {
      showError(`Failed to update target: ${error.message}`);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  // Toggle trailing stop
  const toggleTrailing = async (enabled) => {
    setIsSettingsLoading(true);
    try {
      await updateTrailingStop(enabled, trailingPercent, trailingActivation);
      setTrailingEnabled(enabled);
      showSuccess(
        `Trailing stop ${enabled ? `enabled (${trailingPercent}% trail after ${trailingActivation})` : "disabled"}`
      );
    } catch (error) {
      showError(`Failed to update trailing: ${error.message}`);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  const stepExitPnl = (delta) => {
    const nextValue = parsePnlValue(exitPnlValue, 0) + delta;
    setExitPnlValue(nextValue);

    if (exitPnlEnabled) {
      toggleExitPnl(true, nextValue);
    }
  };

  const stepTargetPnl = (delta) => {
    const nextValue = parsePnlValue(targetPnlValue, 0) + delta;
    setTargetPnlValue(nextValue);

    if (targetPnlEnabled) {
      toggleTargetPnl(true, nextValue);
    }
  };

  // Open trailing modal
  const openTrailingModal = () => {
    setEditTrailingPercent(trailingPercent);
    setEditTrailingActivation(trailingActivation);
    setShowTrailingModal(true);
  };

  // Save trailing settings from modal
  const saveTrailingConfig = async () => {
    setIsSettingsLoading(true);
    try {
      await updateTrailingStop(trailingEnabled, editTrailingPercent, editTrailingActivation);
      setTrailingPercent(editTrailingPercent);
      setTrailingActivation(editTrailingActivation);
      setShowTrailingModal(false);
      showSuccess(`Trailing settings updated: ${editTrailingPercent}% after ${editTrailingActivation}`);
    } catch (error) {
      showError(`Failed to update trailing: ${error.message}`);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  return (
    <div className="trading-controls-panel">
      <button
        className="exit-all-btn"
        onClick={handleExitAll}
        disabled={isExiting || openPositions.length === 0}
      >
        {isExiting ? "Exiting..." : "Exit All"}
      </button>

      {/* Spot/Fut Display */}
      {indexData.spot && (
        <div className="spot-fut-display">
          <span className="spot-info">
            <label>Spot:</label>
            <span className="value">{indexData.spot?.toFixed(1)}</span>
          </span>
          <span className="ohlc-info">
            O:{indexData.spot_open?.toFixed(0)} H:{indexData.spot_high?.toFixed(0)} L:{indexData.spot_low?.toFixed(0)}
          </span>
          <span className="fut-info">
            <label>Fut:</label>
            <span className="value">{indexData.synthetic_future?.toFixed(1)}</span>
            <span className={`basis ${(indexData.synthetic_future - indexData.spot) >= 0 ? 'positive' : 'negative'}`}>
              ({(indexData.synthetic_future - indexData.spot) >= 0 ? '+' : ''}{(indexData.synthetic_future - indexData.spot)?.toFixed(1)})
            </span>
          </span>
        </div>
      )}

      {/* Token Creation Time */}
      <div className="token-health-section">
        <div className="token-status-dot" title="Token created at" />
        <span className="token-time">
          {tokenHealth?.created_at ? formatTokenTime(tokenHealth.created_at) : "--:--"}
        </span>
      </div>

      <div className="limit-order-controls">
        <span className="control-label">Limit:</span>
        <div className="button-group">
          <button
            className={!limitOrderEnabled ? "active" : ""}
            onClick={() => handleToggleLimitOrder(false)}
            disabled={isSettingsLoading}
          >
            Off
          </button>
          {[0.25, 0.5, 1].map((percent) => (
            <button
              key={percent}
              className={limitOrderEnabled && limitOrderPercent === percent ? "active" : ""}
              onClick={() => handleToggleLimitOrder(true, percent)}
              disabled={isSettingsLoading}
            >
              {percent}%
            </button>
          ))}
        </div>
      </div>

      <div className="pnl-controls exit-pnl">
        <span className="control-label">Stop:</span>
        <div className="pnl-input-container">
          <input
            type="checkbox"
            className="pnl-toggle"
            checked={exitPnlEnabled}
            onChange={(e) => toggleExitPnl(e.target.checked, exitPnlValue)}
            disabled={isSettingsLoading}
          />
          <button
            type="button"
            className="pnl-step-btn"
            onClick={() => stepExitPnl(-PNL_STEP_SIZE)}
            disabled={isSettingsLoading}
            aria-label={`Decrease stop loss by ${PNL_STEP_SIZE}`}
            title={`Decrease stop loss by ${PNL_STEP_SIZE}`}
          >
            -
          </button>
          <input
            type="text"
            className={`pnl-input ${editingExitPnl ? "editing" : ""} ${exitPnlValue !== lastSavedExitValue ? "unsaved" : ""}`}
            value={exitPnlValue}
            onChange={(e) => {
              const inputVal = e.target.value;
              if (inputVal === "" || inputVal === "-") {
                setExitPnlValue(inputVal);
                setEditingExitPnl(true);
                return;
              }
              const value = parseInt(inputVal, 10);
              if (!isNaN(value)) {
                setExitPnlValue(value);
                setEditingExitPnl(true);
                if (exitPnlEnabled && value !== lastSavedExitValue) {
                  toggleExitPnl(false, value);
                }
              }
            }}
            onFocus={() => setEditingExitPnl(true)}
            onBlur={() => {
              if (exitPnlValue === "" || exitPnlValue === "-") {
                setExitPnlValue(0);
              }
              setEditingExitPnl(false);
            }}
            disabled={isSettingsLoading}
          />
          <button
            type="button"
            className="pnl-step-btn"
            onClick={() => stepExitPnl(PNL_STEP_SIZE)}
            disabled={isSettingsLoading}
            aria-label={`Increase stop loss by ${PNL_STEP_SIZE}`}
            title={`Increase stop loss by ${PNL_STEP_SIZE}`}
          >
            +
          </button>
        </div>
      </div>

      <div className="pnl-controls target-pnl">
        <span className="control-label">Target:</span>
        <div className="pnl-input-container">
          <input
            type="checkbox"
            className="pnl-toggle"
            checked={targetPnlEnabled}
            onChange={(e) => toggleTargetPnl(e.target.checked, targetPnlValue)}
            disabled={isSettingsLoading}
          />
          <button
            type="button"
            className="pnl-step-btn"
            onClick={() => stepTargetPnl(-PNL_STEP_SIZE)}
            disabled={isSettingsLoading}
            aria-label={`Decrease target by ${PNL_STEP_SIZE}`}
            title={`Decrease target by ${PNL_STEP_SIZE}`}
          >
            -
          </button>
          <input
            type="text"
            className={`pnl-input ${editingTargetPnl ? "editing" : ""} ${targetPnlValue !== lastSavedTargetValue ? "unsaved" : ""}`}
            value={targetPnlValue}
            onChange={(e) => {
              const inputVal = e.target.value;
              if (inputVal === "" || inputVal === "-") {
                setTargetPnlValue(inputVal);
                setEditingTargetPnl(true);
                return;
              }
              const value = parseInt(inputVal, 10);
              if (!isNaN(value)) {
                setTargetPnlValue(value);
                setEditingTargetPnl(true);
                if (targetPnlEnabled && value !== lastSavedTargetValue) {
                  toggleTargetPnl(false, value);
                }
              }
            }}
            onFocus={() => setEditingTargetPnl(true)}
            onBlur={() => {
              if (targetPnlValue === "" || targetPnlValue === "-") {
                setTargetPnlValue(0);
              }
              setEditingTargetPnl(false);
            }}
            disabled={isSettingsLoading}
          />
          <button
            type="button"
            className="pnl-step-btn"
            onClick={() => stepTargetPnl(PNL_STEP_SIZE)}
            disabled={isSettingsLoading}
            aria-label={`Increase target by ${PNL_STEP_SIZE}`}
            title={`Increase target by ${PNL_STEP_SIZE}`}
          >
            +
          </button>
        </div>
      </div>

      <div className="pnl-controls trailing-stop">
        <span className="control-label">Trail:</span>
        <div className="pnl-input-container">
          <input
            type="checkbox"
            className="pnl-toggle"
            checked={trailingEnabled}
            onChange={(e) => toggleTrailing(e.target.checked)}
            disabled={isSettingsLoading}
          />
          <span
            className="trailing-display clickable"
            onClick={openTrailingModal}
            title="Click to configure"
          >
            {trailingPercent}%@{trailingActivation}
          </span>
          <span className="peak-display">
            Peak: {peakPnl.toFixed(0)}
          </span>
        </div>
      </div>

      {/* Trailing Stop Configuration Modal */}
      {showTrailingModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowTrailingModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trailing Stop Configuration</h3>
              <button
                className="modal-close"
                onClick={() => setShowTrailingModal(false)}
              >
                x
              </button>
            </div>
            <div className="modal-body">
              <div className="config-section">
                <label>
                  <span className="config-label">Trailing Percentage:</span>
                  <input
                    type="number"
                    min="5"
                    max="55"
                    step="5"
                    value={editTrailingPercent}
                    onChange={(e) =>
                      setEditTrailingPercent(parseInt(e.target.value) || 25)
                    }
                    className="config-input"
                  />
                  <span className="config-hint">% (5-55)</span>
                </label>
                <p className="config-description">
                  Locks {100 - editTrailingPercent}% of peak profit. Lower % =
                  tighter protection.
                </p>
              </div>

              <div className="config-section">
                <label>
                  <span className="config-label">Activate After:</span>
                  <input
                    type="number"
                    min="100"
                    max="10000"
                    step="500"
                    value={editTrailingActivation}
                    onChange={(e) =>
                      setEditTrailingActivation(parseInt(e.target.value) || 500)
                    }
                    className="config-input"
                  />
                  <span className="config-hint">Rs (100-10,000)</span>
                </label>
                <p className="config-description">
                  Start trailing after this profit is reached.
                </p>
              </div>

              <div className="config-section">
                <div className="config-info">
                  <strong>Example:</strong> With {editTrailingPercent}% trailing
                  after Rs{editTrailingActivation}:
                  <br />
                  If peak reaches Rs1,000, trailing stop = Rs
                  {(1000 * (1 - editTrailingPercent / 100)).toFixed(0)}
                  <br />
                  Locks minimum {100 - editTrailingPercent}% of peak profit.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowTrailingModal(false)}
                disabled={isSettingsLoading}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={saveTrailingConfig}
                disabled={isSettingsLoading}
              >
                {isSettingsLoading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingControlsPanel;
